import { useCanWrite } from '@artic/api-client';
import { ConfirmActionFooter, ConfirmActionModal } from '@artic/ui/confirm-action-modal';
import { DialogDescription, DialogHeader, DialogTitle } from '@artic/ui/dialog';
import { toast } from '@artic/ui/toast';
import { useTranslation } from 'react-i18next';

import { apiErrorMessage } from '../_shared/api';
import { useArchiveProject } from './queries';

interface ArchiveProjectDialogProps {
  project: { project_id: string; project_name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ENG-981 confirmation for archiving a project. Only reachable when the list
 * says the project has no work orders outside draft; the server re-checks, so
 * a list that went stale in between surfaces the 409's reason as a toast.
 */
export function ArchiveProjectDialog({ project, open, onOpenChange }: ArchiveProjectDialogProps) {
  const { t } = useTranslation('project');
  const canWrite = useCanWrite('planner');
  const archive = useArchiveProject();
  const name = project.project_name;

  function handleConfirm() {
    // Dialogs are portaled, so a fieldset wrapping the trigger doesn't reach
    // this form (permissions v1) — guard the submit directly.
    if (!canWrite) return;
    archive.mutate(project.project_id, {
      onSuccess: () => {
        toast.success(t('archive.success', { name }));
        onOpenChange(false);
      },
      onError: (error) => {
        toast.error(apiErrorMessage(error, t('archive.failed')));
      },
    });
  }

  return (
    <ConfirmActionModal open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>{t('archive.title', { name })}</DialogTitle>
        <DialogDescription>{t('archive.description', { name })}</DialogDescription>
      </DialogHeader>
      <ConfirmActionFooter
        confirmLabel={t('archive.confirm')}
        onConfirm={handleConfirm}
        onCancel={() => onOpenChange(false)}
        pending={archive.isPending}
        confirmDisabled={!canWrite}
      />
    </ConfirmActionModal>
  );
}
